// © 2026 AttainIP Engineering
/**
 * Reading one row of the admin Users list (RFC-0510 W41).
 *
 * A user row exists per **(person, organisation)** — RFC-0510 A7, one identity
 * per human per organisation. So searching one email returns several rows that
 * are DIFFERENT THINGS, and a list that shows only name / email / roles renders
 * them identically. That is not cosmetic: it is how a guest of another firm was
 * granted `org_admin` there, because nothing on the row said "guest".
 *
 * ⚠️ **TYPE AND ROLE ARE TWO AXES.** Core's role landscape (2026-09-02) is
 * explicit: the account type says what KIND of identity this is and carries no
 * permissions; the roles say what the person MAY DO. An org admin is *also* an
 * enterprise user; a platform admin is *also* an enterprise user. A single
 * "kind" column would have to lie about one of them, so this module never
 * merges them — `rowType` reads one axis and knows nothing about roles.
 *
 * ⚠️ **GUEST IS NOT ON THE USER ROW.** A guest's user row says `enterprise`
 * for the host organisation, deliberately (W18 records nothing guest-shaped on
 * it). The guest fact lives in the membership, which the API joins in. Reading
 * guest-ness off `account_type` is the original bug; `rowType` therefore looks
 * at the membership FIRST and the user row only after.
 *
 * ⚠️ **TIER 5 IS ABSENT ON PURPOSE.** Full engine admin is an Azure AD group
 * the identity service deliberately knows nothing about (Core's landscape,
 * §"The segregation"). It reaches no JWT, no user row and no column here.
 * Showing it would imply identity governs it. `platform_admin` in the Role
 * column is tier 4 — product-scoped — and is a different thing.
 */

import type { OrgChoiceLike } from './orgChoiceLabel';

/** The organisation summary the API attaches to each row. */
export interface RowOrganisation {
  id: string;
  ext_id?: string | null;
  name?: string | null;
  org_type?: string | null;
}

/** The membership the API joins in — the ONLY place guest-ness lives. */
export interface RowMembership {
  /** `member` | `guest` (identity.org_memberships.kind). */
  kind?: string | null;
  /** `active` | `revoked`. A revoked guest keeps their user row. */
  status?: string | null;
  /** A guest's origin firm; null for a member. */
  home_organization?: RowOrganisation | null;
}

/** One row of `GET /admin/v1/users`, as much of it as this module reads. */
export interface UserDirectoryRow {
  organization_id?: string | null;
  organization?: RowOrganisation | null;
  /**
   * The KIND of identity. A `string`, never a union: the platform contract
   * already names types this build does not mint (`mvp`), and more may follow.
   * An unrecognised value must be SHOWN, not coerced.
   */
  account_type?: string | null;
  membership?: RowMembership | null;
  roles?: string[] | null;
}

/**
 * The account types this build has a name for.
 *
 * `mvp` is here even though nothing on beta carries it: it is a real type in
 * the platform contract (Core accepts `org_admin` for it), and treating a real
 * type as unrecognised would cry wolf. Anything NOT in this set is shown as
 * itself and flagged — see `rowType`.
 *
 * `guest` is in the vocabulary but never appears on a user row; it is produced
 * by the membership join.
 */
export const KNOWN_ACCOUNT_TYPES = ['selfserve', 'enterprise', 'guest', 'mvp'] as const;

/** Identity's `MembershipKind.GUEST`. */
const GUEST = 'guest';

/** Identity's `MembershipStatus.ACTIVE`. */
const ACTIVE = 'active';

export interface RowType {
  /** What to render. Never empty. */
  label: string;
  /** The value behind the label; `''` when nothing at all is known. */
  value: string;
  /** false ⇒ this build has no name for it. Render it as a warning. */
  recognised: boolean;
  /** Which fact produced it. `none` means neither the membership nor the user row said anything. */
  source: 'membership' | 'account_type' | 'none';
}

/**
 * What KIND of identity this row is — one axis, and never the roles.
 *
 * The membership is consulted first and wins: a guest's user row says
 * `enterprise`, so trusting the user row here would hide exactly the fact an
 * operator is looking for. A membership that is not a guest adds nothing the
 * user row does not already say, so it defers.
 */
export function rowType(row: UserDirectoryRow): RowType {
  const raw = row.account_type?.trim() ?? '';

  if (row.membership?.kind?.trim().toLowerCase() === GUEST) {
    return { label: GUEST, value: GUEST, recognised: true, source: 'membership' };
  }

  if (!raw) {
    // Not "selfserve". The API omitted or emptied the field, and guessing a
    // default here would invent a fact about somebody's account.
    return { label: 'unknown', value: '', recognised: false, source: 'none' };
  }

  const recognised = (KNOWN_ACCOUNT_TYPES as readonly string[]).includes(raw);
  return {
    label: recognised ? raw : `unrecognised: ${raw}`,
    value: raw,
    recognised,
    source: 'account_type',
  };
}

/**
 * The row's `account_type` when this build has no name for it, else null.
 *
 * Separate from `rowType` on purpose: a guest membership overrides the type
 * column, and an unrecognised value underneath it would otherwise vanish. The
 * caller renders this alongside, so nothing is ever silently dropped.
 */
export function unrecognisedAccountType(row: UserDirectoryRow): string | null {
  const raw = row.account_type?.trim() ?? '';
  if (!raw) return null;
  return (KNOWN_ACCOUNT_TYPES as readonly string[]).includes(raw) ? null : raw;
}

export interface RowMembershipState {
  kind: string;
  status: string;
  /** true while the membership still permits acting in the organisation. */
  live: boolean;
}

/**
 * The membership behind this row, or null when the API did not resolve one.
 *
 * null means NOT KNOWN — never "not a guest". The caller must not render it as
 * `member`.
 */
export function rowMembership(row: UserDirectoryRow): RowMembershipState | null {
  const membership = row.membership;
  if (!membership) return null;
  const kind = membership.kind?.trim() ?? '';
  const status = membership.status?.trim() ?? '';
  if (!kind && !status) return null;
  return { kind, status, live: status.toLowerCase() === ACTIVE };
}

/**
 * The row's organisation in the shape `orgChoiceLabel` already understands.
 *
 * Reuses the Select-Organisation labelling wholesale — including the
 * "Personal workspace" case and the `Organisation <first 8>` fallback for one
 * the API could not resolve — so a firm reads the same in both places.
 */
export function rowOrgChoice(row: UserDirectoryRow): OrgChoiceLike {
  const org = row.organization;
  return {
    organization_id: org?.id ?? row.organization_id ?? '',
    org_ext_id: org?.ext_id ?? null,
    name: org?.name ?? null,
    org_type: org?.org_type ?? null,
  };
}

/** A guest's home firm as an `OrgChoiceLike`, or null when this is not a guest. */
export function rowHomeOrgChoice(row: UserDirectoryRow): OrgChoiceLike | null {
  const home = row.membership?.home_organization;
  if (!home) return null;
  return {
    organization_id: home.id ?? '',
    org_ext_id: home.ext_id ?? null,
    name: home.name ?? null,
    org_type: home.org_type ?? null,
  };
}

/**
 * What the person MAY DO here — the OTHER axis.
 *
 * Deliberately a plain pass-through of identity's `roles`. It is not filtered
 * against a known set: a role we fail to render is a permission an operator
 * cannot see.
 */
export function rowRoles(row: UserDirectoryRow): string[] {
  return (row.roles ?? []).map((r) => String(r).trim()).filter(Boolean);
}
